import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Link, useParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { findDocByPath } from "./manifest";

// Transform markdown links to internal-docs routes
function transformLink(href: string, currentPath: string): string {
  if (!href || href.startsWith("http") || href.startsWith("#")) {
    return href;
  }

  // Remove .md extension
  let path = href.replace(/\.md$/, "");

  // Handle relative paths
  if (!path.startsWith("/") && !path.startsWith("docs/")) {
    // Get the directory of the current document
    const currentDir = currentPath.includes("/")
      ? currentPath.substring(0, currentPath.lastIndexOf("/"))
      : "";

    // Handle ../ navigation. Consume EVERY leading "../" and walk up one
    // directory per segment. `replace("../", "")` stripped only the first, so
    // "../../a" from "x/y/doc" resolved to "x/../a" — one level up instead of
    // two, and the result still carried a traversal segment into the route.
    if (path.startsWith("../")) {
      let dir = currentDir;
      while (path.startsWith("../")) {
        path = path.slice("../".length);
        dir = dir.includes("/") ? dir.substring(0, dir.lastIndexOf("/")) : "";
      }
      path = dir ? `${dir}/${path}` : path;
    } else {
      // Same directory relative link
      path = path.replace(/^\.\//, "");
      path = currentDir ? `${currentDir}/${path}` : path;
    }
  } else {
    // Remove docs/ prefix for absolute-style links
    path = path.replace(/^docs\//, "");
  }

  return `/internal-docs/${path}`;
}

export default function DocsPage() {
  const params = useParams();
  const { getAccessTokenSilently } = useAuth0();
  const [content, setContent] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Build the path from route params
  const path = params["*"] || "";
  const doc = findDocByPath(path);

  useEffect(() => {
    async function loadContent() {
      if (!doc) {
        setError("Document not found");
        setLoading(false);
        return;
      }

      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(
          `${process.env.REACT_APP_API_ROOT}/docs/${doc.file}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (!response.ok) {
          if (response.status === 403) {
            setError("Access denied. Admin role required.");
          } else if (response.status === 404) {
            setError(`Document not found: ${doc.file}`);
          } else {
            setError(`Failed to load document: ${response.statusText}`);
          }
          setLoading(false);
          return;
        }

        const data = await response.json();
        setContent(data.content);
        setLoading(false);
      } catch (err) {
        setError(`Failed to load: ${err}`);
        setLoading(false);
      }
    }

    setLoading(true);
    setError(null);
    loadContent();
  }, [path, doc, getAccessTokenSilently]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  return (
    <article className="docs-article">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ href, children }) => {
            const transformedHref = transformLink(href || "", path);
            // Use React Router Link for internal links
            if (transformedHref.startsWith("/internal-docs")) {
              return <Link to={transformedHref}>{children}</Link>;
            }
            // External links open in new tab
            return (
              <a
                href={transformedHref}
                target="_blank"
                rel="noopener noreferrer"
              >
                {children}
              </a>
            );
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </article>
  );
}
